import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { PopUp } from '@xing-com/crate-jobs-components-pop-up';
import { RadioButton } from '@xing-com/radio-button';
import { RadioButtonGroup } from '@xing-com/radio-button-group';
import { Option } from '@xing-com/text-field';

import type {
  LanguageQuestionId,
  PreScreeningAnswers,
  QuestionId,
} from './pre-screening-dialog.constants';
import {
  languageLevelOptions,
  questions,
  questionTrackingElement,
} from './pre-screening-dialog.constants';
import * as Styled from './pre-screening-dialog.styles';
import { PrescreeningHeadline } from './pre-screening-headline';

export type {
  LanguageQuestionId,
  PreScreeningAnswers,
} from './pre-screening-dialog.constants';

type Props = {
  show: boolean;
  showWorkPermitQuestion?: boolean;
  visibleLanguageQuestionIds?: LanguageQuestionId[];
  onCancel: () => void;
  onConfirm: (answers: PreScreeningAnswers) => void;
};

export const PreScreeningDialog: React.FC<Props> = ({
  show,
  showWorkPermitQuestion = true,
  visibleLanguageQuestionIds = ['germanSkills', 'englishSkills'],
  onCancel,
  onConfirm,
}) => {
  const { formatMessage } = useIntl();
  const { track } = useTracking();
  const [answers, setAnswers] = useState<PreScreeningAnswers>({
    englishSkills: undefined,
    euWorkEligibility: 'yes',
    germanSkills: undefined,
  });

  useEffect(() => {
    if (show) {
      track({
        eventSchema: 'basic',
        event: 'viewed',
        element: 'overlay',
        elementDetail: 'pre_screening_overlay',
        page: 'jobs/detail',
        sentBy: 'jobs',
        type: 'nwt',
      });
    }
  }, [show, track]);

  const trackAnswerChange = (questionId: QuestionId): void => {
    track({
      eventSchema: 'basic',
      event: 'clicked',
      element: questionTrackingElement[questionId],
      elementDetail: 'pre_screening_overlay',
      page: 'jobs/detail',
      sentBy: 'jobs',
      type: 'nwt',
    });
  };

  const handleSaveAndContinueClick = (): void => {
    track({
      eventSchema: 'basic',
      event: 'submitted',
      element: 'pre_screening_questions',
      elementDetail: 'pre_screening_overlay',
      page: 'jobs/detail',
      sentBy: 'jobs',
      type: 'nwt',
    });

    onConfirm(answers);
  };

  const handleCancel = (): void => {
    track({
      eventSchema: 'basic',
      event: 'clicked',
      element: 'close',
      elementDetail: 'pre_screening_overlay',
      page: 'jobs/detail',
      sentBy: 'jobs',
      type: 'nwt',
    });

    onCancel();
  };

  const handleChange =
    (questionId: QuestionId) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      trackAnswerChange(questionId);

      setAnswers((currentAnswers) => ({
        ...currentAnswers,
        [questionId]: event.target.value,
      }));
    };

  const handleLanguageLevelChange =
    (questionId: LanguageQuestionId) =>
    (event: React.ChangeEvent<HTMLSelectElement>): void => {
      trackAnswerChange(questionId);

      setAnswers((currentAnswers) => ({
        ...currentAnswers,
        [questionId]: event.target.value,
      }));
    };

  const visibleQuestions = questions.filter(({ id }) => {
    if (id === 'euWorkEligibility') {
      return showWorkPermitQuestion;
    }

    return visibleLanguageQuestionIds.includes(id);
  });
  const isConfirmDisabled = visibleLanguageQuestionIds.some(
    (questionId) => !answers[questionId]
  );

  return (
    <PopUp onCancel={handleCancel} show={show} showCloseButton>
      <Styled.Content data-testid="pre-screening-dialog">
        <PrescreeningHeadline />
        <Styled.QuestionsContainer>
          {visibleQuestions.map((question) => {
            const label = formatMessage({
              id: question.labelId,
            });

            return (
              <Styled.QuestionFieldset key={question.id}>
                <Styled.QuestionLegend>{label}</Styled.QuestionLegend>
                {question.type === 'radio' ? (
                  <RadioButtonGroup>
                    {question.options.map((option) => (
                      <Styled.OptionLabel
                        key={`${question.id}-${option.value}`}
                      >
                        <RadioButton
                          name={`pre-screening-${question.id}`}
                          value={option.value}
                          on={answers[question.id] === option.value}
                          onChange={handleChange(question.id)}
                        />
                        {formatMessage({
                          id: option.labelId,
                        })}
                      </Styled.OptionLabel>
                    ))}
                  </RadioButtonGroup>
                ) : (
                  <Styled.LanguageLevelDropdown
                    aria-label={label}
                    data-qa={`pre-screening-${question.id}-dropdown`}
                    data-testid={`pre-screening-${question.id}-dropdown`}
                    onChange={handleLanguageLevelChange(question.id)}
                    size="small"
                    value={answers[question.id] ?? ''}
                  >
                    <Option hidden value="" />
                    <Option value="0">-</Option>
                    {languageLevelOptions.map(({ labelId, value }) => (
                      <Option key={value} value={value}>
                        {formatMessage({
                          id: labelId,
                        })}
                      </Option>
                    ))}
                  </Styled.LanguageLevelDropdown>
                )}
              </Styled.QuestionFieldset>
            );
          })}
        </Styled.QuestionsContainer>
        <Styled.LegalText size="small">
          <FormattedMessage id="JOBS_JOB_DETAILS_PRE_SCREENING_LEGAL_DISCLAIMER" />
        </Styled.LegalText>
        <Styled.Actions>
          <Styled.SaveAndContinueButton
            disabled={isConfirmDisabled}
            size="large"
            variant="primary"
            onClick={handleSaveAndContinueClick}
          >
            {formatMessage({
              id: 'JOBS_JOB_DETAILS_PRE_SCREENING_CONFIRM',
            })}
          </Styled.SaveAndContinueButton>
        </Styled.Actions>
      </Styled.Content>
    </PopUp>
  );
};
